import { createSlice } from '@reduxjs/toolkit';
import encoding from 'encoding-japanese';
import apolloClient from '../queries';
import { FetchHotels } from '../queries/index.graphql';
import {
  SORT_ORDERS,
  ROOM_TYPES_QUERY_PARAMS_MAP,
  MEAL_CONDITIONS_QUERY_PARAMS_MAP,
  SORT_ORDERS_QUERY_PARAMS_MAP,
  TAG_IDS_MAP,
  UNEXPECTED_ERROR_MESSAGE,
  MEAL_CONDITIONS,
  ROOM_TYPES,
} from '../constants';
import { saveSearchedHotelsInput, getHotelState } from '../utils/localStorage';
import { sessionKey, setErrorMessages } from './app';
import { setHotelId, setQueryParamsValidatedPlan } from './plan';

const initialState = {
  fetching: false,
  queryParamsValidated: false,
  directions: [],
  hotels: [],
  // paginatorInfo: {
  //   count: undefined,
  //   currentPage: 1,
  //   // firstItem: null,
  //   hasMorePages: false,
  //   // lastItem: null,
  //   lastPage: 1,
  //   perPage: 1,
  //   total: 1,
  // },
  paginatorInfo: undefined,
  group: undefined,
  hotelsInput: {
    stayNights: 1,
    numberOfRooms: 1,
    peoplePerRoom: 1,
    pax: 2,
    directionId: '7',
    prefectureId: '27',
    areaId: undefined,
    districtId: undefined,
    roomType: ROOM_TYPES.none,
    mealCondition: MEAL_CONDITIONS.unspecified,
    tagIds: [],
    tagCodes: [],
    sortOrder: SORT_ORDERS.popularity,
    groupId: undefined,
    hotelCode: undefined,
    provider: undefined,
    tieupCompCD: undefined,
    coopOrganiJoin: undefined,
    priceFrom: undefined,
    priceTo: undefined,
    itemCD: undefined,
    keyword: undefined,
    siteId: undefined,
    searchCondition: undefined,
    showSD: undefined,
    showGroup: undefined,
    hideGroupName: undefined,
  },
  searchedHotelsInput: undefined,
};


const hotelSlice = createSlice({
  name: 'flight',
  initialState,
  reducers: {
    setFetchingHotel: {
      reducer(state, action) {
        const { fetching } = action.payload;
        state.fetching = fetching;
      },
      prepare(fetching) {
        return { payload: { fetching } };
      }
    },
    setQueryParamsValidatedHotel: {
      reducer(state, action) {
        const { validated } = action.payload;
        state.queryParamsValidated = validated;
      },
      prepare(validated) {
        return { payload: { validated } };
      }
    },
    setDirections: {
      reducer(state, action) {
        const { directions } = action.payload;
        state.directions = directions;
      },
      prepare(directions) {
        return { payload: { directions } };
      }
    },
    setHotels: {
      reducer(state, action) {
        const { hotels } = action.payload;
        state.hotels = hotels;
        state.searchedHotelsInput = state.hotelsInput;
        saveSearchedHotelsInput(sessionKey, state.hotelsInput);
      },
      prepare(hotels) {
        return { payload: { hotels } };
      }
    },
    setPaginatorInfo: {
      reducer(state, action) {
        const { paginatorInfo } = action.payload;
        state.paginatorInfo = paginatorInfo;
      },
      prepare(paginatorInfo) {
        return { payload: { paginatorInfo } };
      }
    },
    setGroup: {
      reducer(state, action) {
        const { group } = action.payload;
        state.group = group;
      },
      prepare(group) {
        return { payload: { group } };
      }
    },
    setStayNights: {
      reducer(state, action) {
        const { stayNights } = action.payload;
        state.hotelsInput.stayNights = stayNights;
      },
      prepare(stayNights) {
        return { payload: { stayNights } };
      }
    },
    setPax: {
      reducer(state, action) {
        const { pax } = action.payload;
        state.hotelsInput.pax = pax;
      },
      prepare(pax) {
        return { payload: { pax } };
      }
    },
    setNumberOfRooms: {
      reducer(state, action) {
        const { numberOfRooms } = action.payload;
        state.hotelsInput.numberOfRooms = numberOfRooms;
      },
      prepare(numberOfRooms) {
        return { payload: { numberOfRooms } };
      }
    },
    setPeoplePerRoom: {
      reducer(state, action) {
        const { peoplePerRoom } = action.payload;
        state.hotelsInput.peoplePerRoom = peoplePerRoom;
        state.hotelsInput.pax = peoplePerRoom;
      },
      prepare(peoplePerRoom) {
        return { payload: { peoplePerRoom } };
      }
    },
    setDirectionId: {
      reducer(state, action) {
        const { directionId } = action.payload;
        state.hotelsInput.directionId = directionId;
      },
      prepare(directionId) {
        return { payload: { directionId } };
      }
    },
    setPrefectureId: {
      reducer(state, action) {
        const { prefectureId } = action.payload;
        state.hotelsInput.prefectureId = prefectureId;
      },
      prepare(prefectureId) {
        return { payload: { prefectureId } };
      }
    },
    setAreaId: {
      reducer(state, action) {
        const { areaId } = action.payload;
        state.hotelsInput.areaId = areaId;
      },
      prepare(areaId) {
        return { payload: { areaId } };
      }
    },
    setDistrictId: {
      reducer(state, action) {
        const { districtId } = action.payload;
        state.hotelsInput.districtId = districtId;
      },
      prepare(districtId) {
        return { payload: { districtId } };
      }
    },
    setRoomType: {
      reducer(state, action) {
        const { roomType } = action.payload;
        state.hotelsInput.roomType = roomType;
      },
      prepare(roomType) {
        return { payload: { roomType } };
      }
    },
    setMealCondition: {
      reducer(state, action) {
        const { mealCondition } = action.payload;
        state.hotelsInput.mealCondition = mealCondition;
      },
      prepare(mealCondition) {
        return { payload: { mealCondition } };
      }
    },
    setTagId: {
      reducer(state, action) {
        const { tagId } = action.payload;
        const { tagIds } = state.hotelsInput;
        if (tagIds.includes(tagId)) {
          state.hotelsInput.tagIds = tagIds.filter((c) => c !== tagId);
        } else {
          state.hotelsInput.tagIds.push(tagId);
        }
      },
      prepare(tagId) {
        return { payload: { tagId } };
      }
    },
    setTagCode: {
      reducer(state, action) {
        const { tagCode } = action.payload;
        const { tagCodes } = state.hotelsInput;
        if (tagCodes.includes(tagCode)) {
          state.hotelsInput.tagCodes = tagCodes.filter((c) => c !== tagCode);
        } else {
          state.hotelsInput.tagCodes.push(tagCode);
        }
      },
      prepare(tagCode) {
        return { payload: { tagCode } };
      }
    },
    setSortOrder: {
      reducer(state, action) {
        const { sortOrder } = action.payload;
        state.hotelsInput.sortOrder = sortOrder;
        state.searchedHotelsInput = state.hotelsInput;
        saveSearchedHotelsInput(sessionKey, state.hotelsInput);
      },
      prepare(sortOrder) {
        return { payload: { sortOrder } };
      }
    },
    setSearchedHotelsInput: {
      reducer(state, action) {
        const { hotelsInput } = action.payload;
        state.searchedHotelsInput = hotelsInput || state.hotelsInput;

        // ページロード時、まだsessionKeyがグローバルにセットされていないときは処理しない
        if (sessionKey) {
          saveSearchedHotelsInput(sessionKey, state.searchedHotelsInput);
        }
      },
      prepare(hotelsInput) {
        return { payload: { hotelsInput } };
      }
    },
    setHotelsInput: {
      reducer(state, action) {
        const { hotelsInput } = action.payload;
        state.hotelsInput = hotelsInput || state.hotelsInput;
      },
      prepare(hotelsInput) {
        return { payload: { hotelsInput } };
      }
    },
    setGroupId: {
      reducer(state, action) {
        const { groupId } = action.payload;
        state.hotelsInput.groupId = groupId;
      },
      prepare(groupId) {
        return { payload: { groupId } };
      }
    },
    setHotelCode: {
      reducer(state, action) {
        const { hotelCode } = action.payload;
        state.hotelsInput.hotelCode = hotelCode;
      },
      prepare(hotelCode) {
        return { payload: { hotelCode } };
      }
    },
    setTieupCompCD: {
      reducer(state, action) {
        const { tieupCompCD } = action.payload;
        state.hotelsInput.tieupCompCD = tieupCompCD;
      },
      prepare(tieupCompCD) {
        return { payload: { tieupCompCD } };
      }
    },
    setCoopOrganiJoin: {
      reducer(state, action) {
        const { coopOrganiJoin } = action.payload;
        state.hotelsInput.coopOrganiJoin = coopOrganiJoin;
      },
      prepare(coopOrganiJoin) {
        return { payload: { coopOrganiJoin } };
      }
    },
    setPriceFrom: {
      reducer(state, action) {
        const { priceFrom } = action.payload;
        state.hotelsInput.priceFrom = priceFrom;
      },
      prepare(priceFrom) {
        return { payload: { priceFrom } };
      }
    },
    setPriceTo: {
      reducer(state, action) {
        const { priceTo } = action.payload;
        state.hotelsInput.priceTo = priceTo;
      },
      prepare(priceTo) {
        return { payload: { priceTo } };
      }
    },
    setItemCD: {
      reducer(state, action) {
        const { itemCD } = action.payload;
        state.hotelsInput.itemCD = itemCD;
      },
      prepare(itemCD) {
        return { payload: { itemCD } };
      }
    },
    setKeyword: {
      reducer(state, action) {
        const { keyword } = action.payload;
        state.hotelsInput.keyword = keyword;
      },
      prepare(keyword) {
        return { payload: { keyword } };
      }
    },
    setSiteId: {
      reducer(state, action) {
        const { siteId } = action.payload;
        state.hotelsInput.siteId = siteId;
      },
      prepare(siteId) {
        return { payload: { siteId } };
      }
    },
    setSearchCondition: {
      reducer(state, action) {
        const { searchCondition } = action.payload;
        state.hotelsInput.searchCondition = searchCondition;
      },
      prepare(searchCondition) {
        return { payload: { searchCondition } };
      }
    },
    setShowSD: {
      reducer(state, action) {
        const { showSD } = action.payload;
        state.hotelsInput.showSD = showSD;
      },
      prepare(showSD) {
        return { payload: { showSD } };
      }
    },
    setShowGroup: {
      reducer(state, action) {
        const { showGroup } = action.payload;
        state.hotelsInput.showGroup = showGroup;
      },
      prepare(showGroup) {
        return { payload: { showGroup } };
      }
    },
    setHideGroupName: {
      reducer(state, action) {
        const { hideGroupName } = action.payload;
        state.hotelsInput.hideGroupName = hideGroupName;
      },
      prepare(hideGroupName) {
        return { payload: { hideGroupName } };
      }
    },
  },
});

export const {
  setFetchingHotel,
  setQueryParamsValidatedHotel,
  setDirections,
  setHotels,
  setDetailConditionEnumValues,
  setPaginatorInfo,
  setGroup,
  setStayNights,
  setPax,
  setNumberOfRooms,
  setPeoplePerRoom,
  setDirectionId,
  setPrefectureId,
  setAreaId,
  setDistrictId,
  setRoomType,
  setMealCondition,
  setTagId,
  setTagCode,
  setSortOrder,
  setSearchedHotelsInput,
  setHotelsInput,
  setGroupId,
  setHotelCode,
  setTieupCompCD,
  setCoopOrganiJoin,
  setPriceFrom,
  setPriceTo,
  setItemCD,
  setKeyword,
  setSiteId,
  setSearchCondition,
  setShowSD,
  setShowGroup,
  setHideGroupName,
} = hotelSlice.actions;

export const fetchHotels = (hotelsInput, first, page, callback) => async (dispatch) => {
  dispatch(setFetchingHotel(true));

  try {
    const { data } = await apolloClient.query({
      query: FetchHotels,
      variables: {
        input: hotelsInput,
        first,
        page,
      },
      fetchPolicy: 'no-cache',
    });

    const {
      hotels,
    } = data;
    dispatch(setHotels(hotels.data));
    dispatch(setPaginatorInfo(hotels.paginatorInfo));
    dispatch(setGroup(hotels.group));

  } catch (error) {
    dispatch(setErrorMessages([UNEXPECTED_ERROR_MESSAGE]));
    // eslint-disable-next-line no-console
    console.log(error);
  } finally {
    dispatch(setFetchingHotel(false));

    callback && callback();
  }
};

export const fetchEssentialHotelStateFromSession = (key) => (dispatch) => {
  const hotelState = getHotelState(key);

  const { searchedHotelsInput } = hotelState;
  if (searchedHotelsInput) {
    dispatch(setSearchedHotelsInput(searchedHotelsInput));
    dispatch(setHotelsInput(searchedHotelsInput));
  }
};

export const handleQueryParamsHotel = (queryParams) => (dispatch) => {

  // stayNights, pref, area, distinct, numberOfRooms, peoplePerRoomについてはFlightで処理

  const roomType = ROOM_TYPES_QUERY_PARAMS_MAP[queryParams.RoomType];
  if (roomType) {
    dispatch(setRoomType(roomType));
  }

  const mealCondValue = parseInt(queryParams.MealCond, 10);
  const mealCondition = MEAL_CONDITIONS_QUERY_PARAMS_MAP[mealCondValue];
  if (mealCondition) {
    dispatch(setMealCondition(mealCondition));
  }

  const sortOrder = SORT_ORDERS_QUERY_PARAMS_MAP[queryParams.SortOrder];
  if (sortOrder) {
    dispatch(setSortOrder(sortOrder));
  }

  if (queryParams.Condition) {
    const detailCondtions = queryParams.Condition.split(',');
    detailCondtions.forEach((c) => {
      const tagId = TAG_IDS_MAP[c];
      if (tagId) {
        dispatch(setTagId(tagId));
      } else {
        dispatch(setTagCode(c));
      }
    });
  }

  const groupId = queryParams.GroupID;
  if (groupId) {
    dispatch(setGroupId(groupId));
  }

  // for affiliate
  const HotelId = queryParams.HotelCD;
  if (HotelId) {
    dispatch(setHotelCode(HotelId));

    dispatch(setHotelId(HotelId));
    dispatch(setQueryParamsValidatedPlan(true));
  }

  const tieupCompCD = queryParams.TieupCompCD;
  if (tieupCompCD) {
    dispatch(setTieupCompCD(tieupCompCD));
  }

  const coopOrganiJoin = parseInt(queryParams.COOP_ORGANI_JOIN, 10);
  if (coopOrganiJoin) {
    dispatch(setCoopOrganiJoin(coopOrganiJoin));
  }

  const priceFrom = parseInt(queryParams.PriceFrom, 10);
  if (priceFrom) {
    dispatch(setPriceFrom(priceFrom));
  }

  const priceTo = parseInt(queryParams.PriceTo, 10);
  if (priceTo) {
    dispatch(setPriceTo(priceTo));
  }

  const itemCD = queryParams.ITEM_CD;
  if (itemCD) {
    dispatch(setItemCD(itemCD));
  }

  const keyword = queryParams.Keyword;
  if (keyword) {
    dispatch(setKeyword(keyword));
  }

  const siteId = parseInt(queryParams.SiteID, 10);
  if (typeof siteId === 'number') {
    dispatch(setSiteId(siteId));
  }

  const searchCondition = queryParams.SearchCondition;
  if (searchCondition) {
    dispatch(setSearchCondition(searchCondition));
  }

  const showSD = parseInt(queryParams.ShowSD, 10);
  if (typeof showSD === 'number') {
    dispatch(setShowSD(showSD));
  }

  const showGroup = parseInt(queryParams.ShowGroup, 10);
  if (typeof showGroup === 'number') {
    dispatch(setShowGroup(showGroup));
  }

  const hideGroupName = parseInt(queryParams.HideGroupName, 10);
  if (typeof hideGroupName === 'number') {
    dispatch(setHideGroupName(hideGroupName));
  }

  dispatch(setQueryParamsValidatedHotel(true));
};

export default hotelSlice.reducer;
